import React, { useMemo } from 'react';
import type { NavBarProps } from '../../../NavBar';
import { NavBar } from '../../../NavBar';
import type { EventInfoFragmentFragment } from '@graphql/fragments/event.info.graphql.generated';
import type { EventPageFragment } from '@graphql/fragments/event.page.graphql.generated';
import type { EventPageType } from '@graphql/generated-base';
import { Category } from '@graphql/generated-base';
import { StyledRoot, StyledTitle, styles } from './EventNameNav.styles';
import { Box, ButtonV2, Flex, TextV2 } from '@withjoy/joykit';
import { Link } from '@react-router';
import { GuestLanguageSelector } from '@apps/guest/components/GuestLanguageSelector';
import { useEventUserRole } from '@shared/components/AuthProvider';
import { useTranslation } from '@shared/core/i18n';
import { isInIframe } from '@shared/utils/isInIframe';
import type { NavbarEventPropsInterface } from '@apps/guest/packages/layout-engine/layouts/layout.types';
import { animationTransition } from '@shared/utils/animationTransition';
import { useMediaQuery } from '@withjoy/joykit/utils';
import { InlineEditor } from '@apps/guest/shared/components/InlineEditor';
import { sendMessageToParentWindow } from '@shared/utils/previewMessageBus';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { GuestSiteTypographyOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride';
import type { OverrideTypography } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride.types';
import { Edit } from '@withjoy/joykit/icons';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';
import { useColorPaletteProvider } from '@apps/guest/packages/layout-engine/layouts/ColorPaletteProvider/ColorPaletteProvider';
import { getButtonPropsBasedOnBackground } from '@apps/guest/packages/layout-engine/layouts/layout.utils';
import { CustomArtMobile } from '@apps/guest/packages/layout-engine/components/CustomArtMobile/CustomArtMobile';
import { useBrannanDesignContext } from '@apps/guest/packages/layout-engine/layouts/LayoutBrannan/BrannanDesignContext';
import { getBrannanMobileMediaQuery } from '@apps/guest/packages/layout-engine/layouts/LayoutBrannan/LayoutBrannan.theme';
import { useMount } from '@shared/utils/hooks/useMount';
import { getMastheadContainerCss, getMastheadLayerCss, MASTHEAD_TITLE_PADDING_Y } from '../Masthead/masthead.styles';
import type { ResolvedMasthead } from '@shared/types/Masthead.types';
import type { WebsiteHeaderEventTitle } from '@shared/types/WebsiteHeaderSettings.types';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';

export interface EventNameNavProps {
  eventHandle: string;
  info: EventInfoFragmentFragment;
  pages: ReadonlyArray<EventPageFragment>;
  navBarVariant: NavBarProps['variant'];
  applicatorProp: object;
  handleMenuClicked?: (item: string) => void;
  handleEditEventClicked?: () => void;
  eventProps: NavbarEventPropsInterface;
  page: Maybe<EventPageType>;
  pageSlug?: string;
  masthead?: ResolvedMasthead | null;
}

interface Props extends EventNameNavProps {
  eventDisplayName: Maybe<string>;
  headerTextData?: WebsiteHeaderEventTitle;
}

const typographyOverride: OverrideTypography = [
  { variant: 'display6', category: Category.EVENT_TITLE },
  { variant: 'display4', category: Category.EVENT_TITLE },
];

/**
 * This is a straight clone of the Header.tsx component
 */
const EventNameNav: React.FC<Props> = ({
  applicatorProp,
  eventHandle,
  info,
  eventDisplayName,
  headerTextData,
  pages,
  navBarVariant,
  handleMenuClicked,
  handleEditEventClicked,
  eventProps,
  page,
  pageSlug,
  masthead,
  children,
  ...restProps
}) => {
  const { isAdmin } = useEventUserRole();
  const [isMultilineTitleEnabled] = useIsFeatureEnabled('websiteHeaderMultilineTitleEnabled');
  // Prefer the header's own multi-line title whenever it has content; otherwise (flag off or no
  // title set) fall back to the event display name. Presence of content — not a modified flag — is
  // the signal, since the title is seeded/edited directly.
  const hasMultilineHeaderTitle = isMultilineTitleEnabled && !!headerTextData?.content?.trim();
  const titleContent = hasMultilineHeaderTitle ? headerTextData?.content : eventDisplayName;
  const isMobile = useMediaQuery(theme => theme.mediaQueries.between(0, { breakpointAlias: 'sm2' }));
  const isXsScreen = useMediaQuery(theme => theme.mediaQueries.between(0, { breakpointAlias: 'xs' }));
  const isMobileOrTablet = useIsMobileOrTablet();
  const { pageColor, pageFontColor, artBackgroundFontColor } = useColorPaletteProvider();

  const { t2 } = useTranslation('eventMenu');
  const editEventText = t2('editEvent');
  const inlineEditorTransFn = useTranslation('joykit').t('inlineEditor');
  const customEditEventButtonProps = useMemo(() => (pageColor ? { ...getButtonPropsBasedOnBackground(pageColor), startIcon: <Edit size={16} /> } : {}), [pageColor]);
  const pageFontColorProps = useMemo(() => (pageFontColor ? { color: pageFontColor } : {}), [pageFontColor]);
  const mastheadContainerCss = masthead ? getMastheadContainerCss(masthead) : {};
  // In guest view InlineEditor renders bare children, so the static h1 needs its own
  // stacking above the positioned art layer; paddingY keeps the name off the frame art.
  const mastheadTitleProps = masthead ? ({ position: 'relative', zIndex: 1, paddingY: MASTHEAD_TITLE_PADDING_Y } as const) : {};

  const isPreviewing = isInIframe();
  // The xs breakpoint brannan layout 375 when in Iframe and 480 when not in Iframe.
  // This causes difference of height between preview and guest
  const eventNameMinHeight = useMemo(() => [isPreviewing ? 291 : 200, 200, null, 'initial'], [isPreviewing]);

  const isSmallScreen = useMediaQuery(getBrannanMobileMediaQuery);
  const { reapplyDesign } = useBrannanDesignContext();
  useMount(() => {
    // On small screens, the style applicator targets [data-component="title-banner"] which lives
    // inside this lazy-loaded component. Re-apply after mount so the applicator can find the element.
    if (isSmallScreen) {
      reapplyDesign();
    }
  });

  const handleEditTextClick = useEventCallback(() => {
    // The H1 shows the header's own multiline eventName — route the pencil to its editor.
    // editText would edit the event-level display name, a different value.
    if (hasMultilineHeaderTitle) {
      sendMessageToParentWindow({
        action: 'inlineEditingInteraction',
        source: 'joyPreview',
        value: {
          action: 'editHeaderEventTitle',
          inlineEditData: { textContent: headerTextData?.content || '', label: inlineEditorTransFn.displayName() },
        },
      });
      return;
    }
    // send the message to the parent window to open the text editor
    sendMessageToParentWindow({
      action: 'inlineEditingInteraction',
      source: 'joyPreview',
      value: {
        action: 'editText',
        inlineEditData: { textContent: eventDisplayName || '', label: inlineEditorTransFn.displayName(), validationConstraints: { maxLength: 30 } },
      },
    });
  });

  const handleEditFontClick = useEventCallback((category: Category) => {
    sendMessageToParentWindow({
      action: 'inlineEditingInteraction',
      source: 'joyPreview',
      value: { action: 'editFont', inlineEditData: { category } },
    });
  });

  return (
    <>
      <StyledRoot
        {...restProps}
        position={'relative'}
        {...applicatorProp}
        data-uh="nah"
      >
        {isSmallScreen && eventProps.isCustomArtEnabled && <CustomArtMobile backgroundImageUrl={eventProps.customArtUrl} />}
        <Flex
          width="100%"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight={eventNameMinHeight}
        >
          <Box
            height={{ _: 'auto', sm2: 'auto' }}
            transition={animationTransition('height')}
            width="100%"
            {...pageFontColorProps}
          >
            {/*
              When `matchDesktopPageColorForMobileTitleBanner` is on, the mobile banner is painted with `pageColor`
              and the couple can re-pick "Color on top of art" in the designer to regain contrast — that color isn't
              used elsewhere in Brannan.
            */}
            <StyledTitle
              __css={{ ...styles.title, ...mastheadContainerCss }}
              data-testid="header-title"
              color={{ _: artBackgroundFontColor, sm4: 'inherit' }}
            >
              {masthead && (
                // Full-viewport art layer anchored to the title block — breaks out of the
                // 1024px Brannan card the same way PageOrScreenWidthImage does.
                // pointer-events off so InlineEditor stays usable.
                <Box
                  position="absolute"
                  top={0}
                  bottom={0}
                  width="100vw"
                  left="50%"
                  transform="translateX(-50%)"
                  pointerEvents="none"
                  {...getMastheadLayerCss(masthead)}
                />
              )}
              <InlineEditor
                elementLabel={inlineEditorTransFn.displayName()}
                labelPosition="bottom"
                actionData={{
                  edit: handleEditTextClick,
                  font: () => handleEditFontClick(Category.EVENT_TITLE),
                }}
                wrapperCSS={{
                  zIndex: 'banner',
                }}
                inset={isMobile ? -12 : -16}
                componentName="eventDisplayName"
                pageName={page}
                pageSlug={pageSlug || ''}
              >
                <GuestSiteTypographyOverride override={typographyOverride}>
                  <TextV2
                    // The LCP element, large block-level text
                    as="h1"
                    typographyVariant={isXsScreen ? 'display4' : 'display6'}
                    whiteSpace={hasMultilineHeaderTitle ? 'pre-wrap' : undefined}
                    textAlign={hasMultilineHeaderTitle ? (headerTextData?.textAlignment ?? 'center') : undefined}
                    {...mastheadTitleProps}
                  >
                    {titleContent}
                  </TextV2>
                </GuestSiteTypographyOverride>
              </InlineEditor>
            </StyledTitle>
            {/* Header controls, top-right: the guest language picker stacked above the admin
                Edit Event button. Stacking keeps the pair narrow so it doesn't run into the
                event title — the picker renders for guests, Edit Event only for admins on desktop. */}
            <Flex
              position="absolute"
              top="19px"
              right="16px"
              flexDirection="column"
              alignItems="flex-end"
              gap={3}
            >
              <GuestLanguageSelector surface="header" />
              {isAdmin && !isPreviewing && navBarVariant === 'inline' && (
                <ButtonV2
                  onClick={handleEditEventClicked}
                  intent="primary"
                  size="sm"
                  typographyVariant="button1"
                  variant="outline"
                  as={Link}
                  to={`/${eventHandle}/edit/dashboard`}
                  {...customEditEventButtonProps}
                >
                  {editEventText}
                </ButtonV2>
              )}
            </Flex>
            <NavBar
              handleMenuClicked={handleMenuClicked}
              title={eventDisplayName}
              variant={navBarVariant}
              data-testid={'header-nav'}
              eventHandle={eventHandle}
              pages={pages}
              eventProps={eventProps}
            />
            {!isMobileOrTablet && children}
          </Box>
        </Flex>
      </StyledRoot>

      {/* children will typically be the header photo, which should be placed below the component title banner on mobile/tablet*/}
      {isMobileOrTablet && children}
    </>
  );
};

EventNameNav.displayName = 'Header';

export { EventNameNav };
